import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { EnhancedProjectSidebar } from './EnhancedProjectSidebar';
import { ProjectContext } from '~/components/ProjectContext';
import { EnhancedProjectFiles } from './EnhancedProjectFiles';
import { ChatInterface } from '~/components/ChatInterface';
import { CreditManagement } from './credits/CreditManagement';
import { Settings, FileText, MessageSquare, CreditCard } from 'lucide-react';
import { useProjects, useUpdateProject } from '../hooks/useProjects';
import { useAuth } from '../contexts/AuthContext';

export function Dashboard() {
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const { data: projects = [], isLoading } = useProjects();
  const { user } = useAuth();
  const updateProjectMutation = useUpdateProject();

  // Find the active project - ensure projects is an array
  const activeProject = Array.isArray(projects) ? projects.find(p => p.id === activeProjectId) : undefined;

  // Handle project updates
  const handleProjectUpdate = async (projectId: string, updates: any) => {
    try {
      await updateProjectMutation.mutateAsync({ projectId, data: updates });
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  };

  // Set the first project as active if none selected and projects exist
  React.useEffect(() => {
    if (!activeProjectId && Array.isArray(projects) && projects.length > 0) {
      setActiveProjectId(projects[0].id);
    }
  }, [projects, activeProjectId]);

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-background">
      <EnhancedProjectSidebar
        projects={Array.isArray(projects) ? projects : []}
        activeProjectId={activeProjectId}
        onProjectSelect={setActiveProjectId}
        user={user}
      />
      
      <div className="flex-1 flex flex-col">
        <Routes>
          {/* Credits management page */}
          <Route path="/credits" element={<CreditManagement />} />
          
          {/* Main project dashboard */}
          <Route path="/" element={
            activeProject ? (
              <Tabs defaultValue="context" className="flex-1 flex flex-col">
                <div className="border-b bg-card px-6 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h1 className="font-medium">{activeProject.name}</h1>
                      {activeProject.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {activeProject.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <TabsList style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', width: '100%', maxWidth: '384px' }}>
                    <TabsTrigger value="context" className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Context
                    </TabsTrigger>
                    <TabsTrigger value="files" className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Files
                    </TabsTrigger>
                    <TabsTrigger value="chat" className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Chat
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex-1 overflow-hidden">
                  <TabsContent value="context" className="h-full p-6 overflow-y-auto">
                    <ProjectContext
                      project={activeProject}
                      onProjectUpdate={handleProjectUpdate}
                    />
                  </TabsContent>
                  
                  <TabsContent value="files" className="h-full p-6 overflow-y-auto">
                    <EnhancedProjectFiles
                      project={activeProject}
                      projectId={activeProject.id}
                    />
                  </TabsContent>
                  
                  <TabsContent value="chat" className="h-full overflow-hidden">
                    <ChatInterface
                      project={activeProject}
                      projectId={activeProject.id}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center">
                <div className="text-muted-foreground">
                  <Settings className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <h2 className="mb-2">No Project Selected</h2>
                  <p>Select a project from the sidebar or create a new one to get started</p>
                </div>
              </div>
            )
          } />
          
          {/* Redirect unknown routes to main dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </div>
  );
}